import { useCallback, useEffect, useState } from 'react'

import { Box, Title } from '@factorialco/deprecated-design-system'
import { Alert, AlertDescription, AlertTitle } from '@factorialco/f0-react/dist/experimental'
import {
  CreateCompletionConfigurationDocument,
  EditTrainingQueryDocument,
  EditTrainingTrainingFragment,
  UpdateCompletionConfigurationDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CompletionConfigurationFields } from 'modules/trainings/components/Revamp/CompletionConfigurationFields'
import { useEditTrainingContext } from 'modules/trainings/hooks/EditTraining/useEditTrainingContext'

interface CompletionConfigurationData {
  attendancePercentage?: number
  moduleCompletedPercentage?: number
  knowledgeTestRequired: boolean
}

interface CompletionConfigurationProps {
  saved: boolean
  training: EditTrainingTrainingFragment
}

export const CompletionConfiguration = ({ saved, training }: CompletionConfigurationProps) => {
  const { company } = useCurrentSession()
  const { setSaved, setIsDirty, setIsDisabled } = useEditTrainingContext()

  const [createMutation, { loading: loadingCreate }] = useMutation(
    CreateCompletionConfigurationDocument,
    {
      extractorFn: (data) => data.trainingsMutations.createTrainingsCompletionConfiguration,
      refetchQueries: [EditTrainingQueryDocument],
    }
  )

  const [updateMutation, { loading: loadingUpdate }] = useMutation(
    UpdateCompletionConfigurationDocument,
    {
      extractorFn: (data) => data.trainingsMutations.updateTrainingsCompletionConfiguration,
      refetchQueries: [EditTrainingQueryDocument],
    }
  )

  const existingConfig = training.completionConfiguration
  const readonly = !training._policies?.edit.can

  const getInitialAttendancePercentage = (): number | undefined => {
    if (existingConfig?.attendancePercentage != null) return existingConfig.attendancePercentage

    // If there's another config active, return undefined. Otherwise, return 100 as default.
    const otherConfigActive =
      existingConfig?.knowledgeTestRequired || existingConfig?.moduleCompletedPercentage
    return otherConfigActive ? undefined : 100
  }

  const [formData, setFormData] = useState<CompletionConfigurationData>({
    attendancePercentage: getInitialAttendancePercentage(),
    moduleCompletedPercentage: existingConfig?.moduleCompletedPercentage ?? undefined,
    knowledgeTestRequired: existingConfig?.knowledgeTestRequired ?? false,
  })

  const [initialData, setInitialData] = useState<CompletionConfigurationData>({
    attendancePercentage: getInitialAttendancePercentage(),
    moduleCompletedPercentage: existingConfig?.moduleCompletedPercentage ?? undefined,
    knowledgeTestRequired: existingConfig?.knowledgeTestRequired ?? false,
  })

  const handleDataChanged = (newData: CompletionConfigurationData) => {
    setFormData(newData)
  }

  const handleSave = useCallback(async () => {
    // Reset saved flag immediately to prevent infinite loop
    setSaved(false)

    const hasAnyConfig =
      formData.attendancePercentage !== undefined ||
      formData.moduleCompletedPercentage !== undefined ||
      formData.knowledgeTestRequired

    if (!hasAnyConfig && !existingConfig) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'trainings.trainings_revamp.new_training.wizard.completion_configuration.no_config_error'
        ),
      })
      return
    }

    try {
      if (existingConfig) {
        // Update existing configuration
        const result = await updateMutation({
          variables: {
            id: Number(existingConfig.id),
            attendancePercentage: formData.attendancePercentage,
            moduleCompletedPercentage: formData.moduleCompletedPercentage,
            knowledgeTestRequired: formData.knowledgeTestRequired,
          },
        })

        const handled = handleResult(result, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t(
              'trainings.trainings_revamp.new_training.wizard.completion_configuration.update_error'
            ),
          })
        })

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t(
              'trainings.trainings_revamp.new_training.wizard.completion_configuration.update_success'
            ),
          })
          setInitialData(formData)
          setIsDirty(false)
        }
      } else {
        // Create new configuration
        const result = await createMutation({
          variables: {
            trainingId: Number(training.id),
            companyId: company.id,
            attendancePercentage: formData.attendancePercentage,
            moduleCompletedPercentage: formData.moduleCompletedPercentage,
            knowledgeTestRequired: formData.knowledgeTestRequired,
          },
        })

        const handled = handleResult(result, () => {
          app.setFlash({
            type: 'wrong',
            message: i18n.t(
              'trainings.trainings_revamp.new_training.wizard.completion_configuration.create_error'
            ),
          })
        })

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t(
              'trainings.trainings_revamp.new_training.wizard.completion_configuration.create_success'
            ),
          })
          setInitialData(formData)
          setIsDirty(false)
        }
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'trainings.trainings_revamp.new_training.wizard.completion_configuration.generic_error'
        ),
      })
    }
  }, [
    formData,
    existingConfig,
    updateMutation,
    createMutation,
    training.id,
    company.id,
    setSaved,
    setIsDirty,
    setInitialData,
  ])

  useEffect(() => {
    if (saved && !loadingCreate && !loadingUpdate) {
      handleSave()
    }
  }, [saved, loadingCreate, loadingUpdate, handleSave])

  useEffect(() => {
    const isDirty =
      formData.attendancePercentage !== initialData.attendancePercentage ||
      formData.moduleCompletedPercentage !== initialData.moduleCompletedPercentage ||
      formData.knowledgeTestRequired !== initialData.knowledgeTestRequired
    setIsDirty(isDirty)
  }, [
    formData.attendancePercentage,
    formData.moduleCompletedPercentage,
    formData.knowledgeTestRequired,
    initialData.attendancePercentage,
    initialData.moduleCompletedPercentage,
    initialData.knowledgeTestRequired,
    setIsDirty,
  ])

  useEffect(() => {
    setIsDisabled(loadingCreate || loadingUpdate)
  }, [loadingCreate, loadingUpdate, setIsDisabled])

  return (
    <Box padding="s24" gap="s24">
      <Title
        title={i18n.t(
          'trainings.trainings_revamp.new_training.wizard.completion_configuration.step_title'
        )}
        description={i18n.t(
          'trainings.trainings_revamp.new_training.wizard.completion_configuration.description'
        )}
      />
      <CompletionConfigurationFields
        data={formData}
        onDataChanged={handleDataChanged}
        readonly={readonly}
      />
      <Alert variant="info">
        <AlertTitle>
          {i18n.t(
            'trainings.trainings_revamp.new_training.wizard.completion_configuration.alert_title'
          )}
        </AlertTitle>
        <AlertDescription>
          {i18n.t(
            'trainings.trainings_revamp.new_training.wizard.completion_configuration.alert_description'
          )}
        </AlertDescription>
      </Alert>
      <Box minHeight="s200" />
    </Box>
  )
}
